import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import "./DettaglioGg.css";
import Lf23Loader from "../../../components/Lf23Loader";

const DettaglioGg = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [data, setData] = useState(null);
    const [filterLoading, setFilterLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});
    const [chartFilters, setChartFilters] = useState({
        filiale: null,
        genere: null,
        livello: null,
        contratto: null
    });
    const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 480px)").matches);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 480px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {
                    toDate: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    dettaglioContratto: chartFilters.contratto || filters.dettaglioContratto?.map(d => d.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || "",
                    sesso: chartFilters.genere || filters.sesso?.map(s => s.value).join(",") || "",
                    eta: filters.eta?.map(e => e.value).join(",") || "",
                    filiale: chartFilters.filiale || "",
                    livello: chartFilters.livello || ""
                };
                const res = await axios.get(`${API_URL}/hr-dettaglio/dettaglio-gg`, { params });
                setData(res.data);
            } catch (error) {
                console.error("Errore nel caricamento dati Dettaglio GG:", error);
            } finally {
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters, chartFilters]);

    const isLoading = parentLoading || filterLoading;

    // Toggle righe gerarchiche
    const toggleRow = useCallback((rowKey) => {
        setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
    }, []);

    // Click handlers per cross-filtering sui grafici
    const handleFilialeClick = useCallback((params) => {
        setChartFilters(prev => ({
            ...prev,
            filiale: prev.filiale === params.name ? null : params.name
        }));
    }, []);

    const handleGenereClick = useCallback((params) => {
        setChartFilters(prev => ({
            ...prev,
            genere: prev.genere === params.name ? null : params.name
        }));
    }, []);

    const handleLivelloClick = useCallback((params) => {
        setChartFilters(prev => ({
            ...prev,
            livello: prev.livello === params.name ? null : params.name
        }));
    }, []);

    const handleContrattoClick = useCallback((params) => {
        setChartFilters(prev => ({
            ...prev,
            contratto: prev.contratto === params.name ? null : params.name
        }));
    }, []);

    // Costruisce albero gerarchico a 4 livelli: desc_settore → commessa → attività → ruolo
    const allocazioniTree = useMemo(() => {
        const rows = data?.allocazioni || [];
        const tree = {};
        rows.forEach(r => {
            // Livello 1: desc_settore
            if (!tree[r.desc_settore]) {
                tree[r.desc_settore] = { assegnazioni: 0, si: 0, no: 0, children: {} };
            }
            tree[r.desc_settore].assegnazioni += r.assegnazioni;
            tree[r.desc_settore].si += r.si;
            tree[r.desc_settore].no += r.no;
            // Livello 2: commessa_nome
            if (!tree[r.desc_settore].children[r.commessa_nome]) {
                tree[r.desc_settore].children[r.commessa_nome] = { assegnazioni: 0, si: 0, no: 0, children: {} };
            }
            tree[r.desc_settore].children[r.commessa_nome].assegnazioni += r.assegnazioni;
            tree[r.desc_settore].children[r.commessa_nome].si += r.si;
            tree[r.desc_settore].children[r.commessa_nome].no += r.no;
            // Livello 3: livello_1 (attività)
            if (!tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1]) {
                tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1] = { assegnazioni: 0, si: 0, no: 0, children: [] };
            }
            tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1].assegnazioni += r.assegnazioni;
            tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1].si += r.si;
            tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1].no += r.no;
            // Livello 4: livello_2 (ruolo) — foglia
            tree[r.desc_settore].children[r.commessa_nome].children[r.livello_1].children.push({
                label: r.livello_2,
                assegnazioni: r.assegnazioni,
                si: r.si,
                no: r.no
            });
        });

        const sortAndConvert = (obj) => Object.entries(obj)
            .sort(([, a], [, b]) => b.assegnazioni - a.assegnazioni)
            .map(([key, node]) => ({ label: key, ...node }));

        return Object.entries(tree)
            .sort(([, a], [, b]) => b.assegnazioni - a.assegnazioni)
            .map(([settore, node]) => ({
                label: settore,
                ...node,
                children: Object.entries(node.children)
                    .sort(([, a], [, b]) => b.assegnazioni - a.assegnazioni)
                    .map(([comm, commNode]) => ({
                        label: comm,
                        ...commNode,
                        children: sortAndConvert(commNode.children)
                    }))
            }));
    }, [data]);

    // Totali per il footer
    const allocazioniTotali = useMemo(() => {
        return allocazioniTree.reduce((acc, node) => ({
            assegnazioni: acc.assegnazioni + node.assegnazioni,
            si: acc.si + node.si,
            no: acc.no + node.no
        }), { assegnazioni: 0, si: 0, no: 0 });
    }, [allocazioniTree]);

    // Configurazioni ECharts

    // Persone per Filiale
    const filialeOptions = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '10%', bottom: '3%', top: '10%', containLabel: true },
        xAxis: {
            type: 'category',
            data: (data?.personePerFiliale || []).map(d => d.name),
            axisLabel: { fontSize: 11 }
        },
        yAxis: { type: 'value' },
        series: [{
            type: 'bar',
            data: (data?.personePerFiliale || []).map(d => ({
                value: d.value,
                itemStyle: {
                    color: '#016FB7',
                    opacity: chartFilters.filiale
                        ? (d.name === chartFilters.filiale ? 1.0 : 0.3)
                        : 1.0
                }
            })),
            label: { show: true, position: 'top', fontWeight: 'bold', fontSize: 12 }
        }]
    };

    // Genere
    const genereColors = ['#016FB7', '#C8A951'];
    const genereOptions = {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        color: genereColors,
        series: [{
            name: 'Genere',
            type: 'pie',
            radius: ['45%', '75%'],
            center: ['50%', '50%'],
            data: (data?.genere || []).map((d, i) => ({
                ...d,
                itemStyle: {
                    color: genereColors[i % genereColors.length],
                    opacity: chartFilters.genere
                        ? (d.name === chartFilters.genere ? 1.0 : 0.3)
                        : 1.0
                }
            })),
            label: {
                show: true,
                formatter: '{c}\n({d}%)',
                fontSize: 12,
                fontWeight: 'bold'
            },
            emphasis: {
                label: { show: true, fontSize: 14, fontWeight: 'bold' }
            }
        }]
    };

    // Monte Ore Medio
    const monteOreOptions = {
        series: [{
            type: 'gauge',
            min: 0,
            max: 38,
            progress: { show: true, width: 14, itemStyle: { color: '#016FB7' } },
            axisLine: { lineStyle: { width: 14, color: [[1, '#e2e8f0']] } },
            axisTick: { show: false },
            splitLine: { show: false },
            splitNumber: 1,
            axisLabel: { show: true, fontSize: 12, color: '#64748b', distance: -28, formatter: (v) => `${v.toFixed(1)}` },
            pointer: { show: false },
            anchor: { show: false },
            title: { show: false },
            detail: {
                valueAnimation: true,
                fontSize: 28,
                fontWeight: 'bold',
                formatter: '{value}',
                offsetCenter: [0, '0%'],
                color: '#0f172a'
            },
            data: [{ value: data?.monteOreMedio || 0 }]
        }]
    };

    // % Svantaggiati
    const svantaggiatiOptions = {
        series: [{
            type: 'gauge',
            min: 25,
            max: 45,
            progress: { show: true, width: 14, itemStyle: { color: '#C8A951' } },
            axisLine: { lineStyle: { width: 14, color: [[1, '#e2e8f0']] } },
            splitNumber: 1,
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: true, fontSize: 12, color: '#64748b', distance: -28, formatter: (v) => `${v.toFixed(1)}%` },
            pointer: { show: false },
            anchor: { show: false },
            title: { show: false },
            detail: {
                valueAnimation: true,
                fontSize: 28,
                fontWeight: 'bold',
                formatter: '{value}%',
                offsetCenter: [0, '0%'],
                color: '#0f172a'
            },
            data: [{ value: data?.svantaggiati?.percentuale || 0 }]
        },
        // Seconda serie: linea target verde a 38%
        {
            type: 'gauge',
            min: 25,
            max: 45,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            progress: { show: false },
            anchor: { show: false },
            title: { show: false },
            detail: {
                show: true,
                offsetCenter: ['83%', '-88%'],
                fontSize: 12,
                fontWeight: 'bold',
                color: '#16a34a',
                formatter: '{value}%'
            },
            pointer: {
                show: true,
                length: '40%',
                width: 3,
                offsetCenter: [0, '-60%'],
                itemStyle: { color: '#16a34a' }
            },
            data: [{ value: 38 }]
        }]
    };

    // Persone per Livello
    const livelloOptions = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '12%', bottom: '3%', top: '3%', containLabel: true },
        xAxis: { type: 'value' },
        yAxis: {
            type: 'category',
            data: (data?.personePerLivello || []).map(d => d.name).reverse(),
            axisLabel: { fontSize: 11 },
            inverse: false
        },
        series: [{
            type: 'bar',
            data: (data?.personePerLivello || []).map(d => ({
                value: d.value,
                itemStyle: {
                    color: '#016FB7',
                    opacity: chartFilters.livello
                        ? (d.name === chartFilters.livello ? 1.0 : 0.3)
                        : 1.0
                }
            })).reverse(),
            label: { show: true, position: 'right', fontSize: 11 }
        }]
    };

    // Persone per Contratto
    const contrattoOptions = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '4%', bottom: isMobile ? '5%' : '25%', top: '10%', containLabel: true },
        xAxis: {
            type: 'category',
            data: (data?.personePerContratto || []).map(d => d.name),
            axisLabel: {
                rotate: 35,
                fontSize: 9,
                overflow: 'truncate',
                width: 100
            }
        },
        yAxis: { type: 'value' },
        series: [{
            type: 'bar',
            data: (data?.personePerContratto || []).map(d => ({
                value: d.value,
                itemStyle: {
                    color: '#016FB7',
                    opacity: chartFilters.contratto
                        ? (d.name === chartFilters.contratto ? 1.0 : 0.3)
                        : 1.0
                }
            })),
            label: { show: true, position: 'top', fontWeight: 'bold', fontSize: 11 }
        }]
    };

    // Render
    if (isLoading) {
        return (
            <Lf23Loader />
        );
    }

    if (!data) {
        return <div className="oper-hr-empty-state">Nessun dato disponibile</div>;
    }

    return (
        <>
            {/* Riga superiore */}
            <div className="dettaglio-gg-kpi-row">

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">Totale Persone</span>
                    <span className="dettaglio-gg-kpi-value">{data.totalePersone}</span>
                </div>

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">Persone per Filiale</span>
                    <ReactECharts option={filialeOptions} style={{ height: '170px', width: '100%' }} onEvents={{ click: handleFilialeClick }} />
                </div>

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">Genere</span>
                    <ReactECharts option={genereOptions} style={{ height: '170px', width: '100%' }} onEvents={{ click: handleGenereClick }} />
                </div>

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">Età Media</span>
                    <span className="dettaglio-gg-kpi-value">{data.etaMedia}</span>
                </div>

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">Monte Ore Medio</span>
                    <ReactECharts option={monteOreOptions} style={{ height: '170px', width: '100%' }} />
                </div>

                <div className="dettaglio-gg-kpi-card">
                    <span className="dettaglio-gg-kpi-label">% Svantaggiati</span>
                    <ReactECharts option={svantaggiatiOptions} style={{ height: '170px', width: '100%' }} />
                </div>
            </div>

            {/* Riga inferiore */}
            <div className="dettaglio-gg-bottom-row">

                <div className="dettaglio-gg-panel">
                    <span className="dettaglio-gg-panel-title">Allocazioni</span>
                    <div className="dettaglio-gg-table-wrapper">
                        <table className="dettaglio-gg-table">
                            <thead>
                                <tr>
                                    <th>Descrizione Settore</th>
                                    <th>Assegnazioni</th>
                                    <th>SI</th>
                                    <th>NO</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocazioniTree.map((settoreNode) => {
                                    const settoreKey = settoreNode.label;
                                    const isSettoreOpen = expandedRows[settoreKey] === true;
                                    return (
                                        <React.Fragment key={settoreKey}>
                                            {/* Livello 1: desc_settore */}
                                            <tr className="tr-level-1">
                                                <td
                                                    className="td-settore td-expandable"
                                                    onClick={() => toggleRow(settoreKey)}
                                                >
                                                    <span className="expand-icon">{isSettoreOpen ? '▼' : '▶'}</span>
                                                    <span className="level-value">{settoreKey}</span>
                                                </td>
                                                <td>{settoreNode.assegnazioni}</td>
                                                <td>{settoreNode.si}</td>
                                                <td>{settoreNode.no}</td>
                                            </tr>
                                            {/* Livello 2: commessa */}
                                            {isSettoreOpen && settoreNode.children.map((commNode) => {
                                                const commKey = `${settoreKey}|${commNode.label}`;
                                                const isCommOpen = expandedRows[commKey] === true;
                                                const hasChildren = commNode.children.length > 0;
                                                return (
                                                    <React.Fragment key={commKey}>
                                                        <tr className="tr-level-2">
                                                            <td
                                                                className={`td-settore td-indent-1 ${hasChildren ? 'td-expandable' : ''}`}
                                                                onClick={() => hasChildren && toggleRow(commKey)}
                                                            >
                                                                <span className="expand-icon">{hasChildren ? (isCommOpen ? '▼' : '▶') : '•'}</span>
                                                                <span className="level-value">{commNode.label}</span>
                                                            </td>
                                                            <td>{commNode.assegnazioni}</td>
                                                            <td>{commNode.si}</td>
                                                            <td>{commNode.no}</td>
                                                        </tr>
                                                        {/* Livello 3: attività (livello_1) */}
                                                        {isCommOpen && commNode.children.map((attNode) => {
                                                            const attKey = `${commKey}|${attNode.label}`;
                                                            const isAttOpen = expandedRows[attKey] === true;
                                                            const hasRuoli = attNode.children.length > 0;
                                                            return (
                                                                <React.Fragment key={attKey}>
                                                                    <tr className="tr-level-3">
                                                                        <td
                                                                            className={`td-settore td-indent-2 ${hasRuoli ? 'td-expandable' : ''}`}
                                                                            onClick={() => hasRuoli && toggleRow(attKey)}
                                                                        >
                                                                            <span className="expand-icon">{hasRuoli ? (isAttOpen ? '▼' : '▶') : '•'}</span>
                                                                            <span className="level-value">{attNode.label}</span>
                                                                        </td>
                                                                        <td>{attNode.assegnazioni}</td>
                                                                        <td>{attNode.si}</td>
                                                                        <td>{attNode.no}</td>
                                                                    </tr>
                                                                    {/* Livello 4: ruolo (livello_2) */}
                                                                    {isAttOpen && attNode.children.map((ruoloNode, ri) => (
                                                                        <tr key={`${attKey}-${ri}`} className="tr-level-4">
                                                                            <td className="td-settore td-indent-3">
                                                                                <span className="level-value">{ruoloNode.label}</span>
                                                                            </td>
                                                                            <td>{ruoloNode.assegnazioni}</td>
                                                                            <td>{ruoloNode.si}</td>
                                                                            <td>{ruoloNode.no}</td>
                                                                        </tr>
                                                                    ))}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Totale</strong></td>
                                    <td><strong>{allocazioniTotali.assegnazioni}</strong></td>
                                    <td><strong>{allocazioniTotali.si}</strong></td>
                                    <td><strong>{allocazioniTotali.no}</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div className="dettaglio-gg-panel">
                    <span className="dettaglio-gg-panel-title">Persone per Livello</span>
                    <ReactECharts option={livelloOptions} style={{ height: '350px', width: '100%' }} onEvents={{ click: handleLivelloClick }} />
                </div>

                <div className="dettaglio-gg-panel">
                    <span className="dettaglio-gg-panel-title">Persone per Contratto</span>
                    <ReactECharts option={contrattoOptions} style={{ height: isMobile ? '200px' : '350px', width: '100%' }} onEvents={{ click: handleContrattoClick }} />
                </div>
            </div>
        </>
    );
};

export default DettaglioGg;
